import React, { useEffect } from "react";
import {
    HeaderContainer,
    HeaderText,
    TitleWrapper,
    TitleRowWrapper,
    HeaderLottieContainer,
    MagicItemsHeaderWrapper,
    MagicItemsTitle,
    MagicItemsText,
    MagicItemsHeaderContainer,
    LockedHeaderContainer,
    LockedTextContainer,
    LockedMessageText,
    EOMessageWrapper,
    AllAddedMessageBox,
    LockedHeaderIcon,
} from "./crossSellHeaderContainer.style";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { LottieAnimation, replaceText } from "@swiggy-private/core-ui";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import {
    lottieProps,
    CROSS_SELL_HEADER_ICON_DIMENSIONS,
    MAGIC_ITEMS_LOCKED_HEADER_ICON_DIMENSIONS,
    SURPRISE_DEALS_TITLE,
    MAGIC_ITEMS_INLINE_ICON_KEY,
} from "./constants";
import { CrossSellPresentationInfo } from "@swiggy-private/cart-types";
import type { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

interface CrossSellHeaderContainerProps {
    presentationInfo: CrossSellPresentationInfo;
    playLottie: boolean;
    canPlayLottie: boolean;
    isMagicItems?: boolean;
    isLocked?: boolean;
    title?: string;
    onTimerComplete?: () => void;
    allCrossSellSuggestionsAdded?: boolean;
    allCrossSellSuggestionsAddedText?: string;
    timerPillComponent?: React.ReactNode;
}

const CHARACTER_ANIMATION_DELAY = 1500;

export const CrossSellHeaderContainer: React.FC<CrossSellHeaderContainerProps> = ({
    presentationInfo,
    playLottie,
    canPlayLottie,
    isMagicItems = false,
    isLocked = false,
    title,
    allCrossSellSuggestionsAdded = false,
    allCrossSellSuggestionsAddedText,
    timerPillComponent,
}) => {
    const [showCharacterAnimation, setShowCharacterAnimation] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const characterTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const text = presentationInfo?.header_data?.subtitle?.text || "";

    useEffect(() => {
        if (playLottie) {
            characterTimeoutRef.current = setTimeout(() => {
                setShowCharacterAnimation(true);
            }, CHARACTER_ANIMATION_DELAY);
        }
        return () => {
            if (characterTimeoutRef.current) {
                clearTimeout(characterTimeoutRef.current);
                characterTimeoutRef.current = null;
            }
        };
    }, [playLottie]);

    if (isMagicItems && allCrossSellSuggestionsAdded && !isEmpty(allCrossSellSuggestionsAddedText)) {
        return (
            <MagicItemsHeaderContainer>
                <LockedHeaderContainer>
                    <AllAddedMessageBox variant="V2.body2.neutral.bold">
                        {replaceText(allCrossSellSuggestionsAddedText, MAGIC_ITEMS_INLINE_ICON_KEY, true)}
                    </AllAddedMessageBox>
                    {timerPillComponent}
                </LockedHeaderContainer>
            </MagicItemsHeaderContainer>
        );
    }

    // MAGIC ITEMS - LOCKED STATE
    if (isMagicItems && isLocked) {
        return (
            <MagicItemsHeaderContainer>
                <LockedHeaderContainer>
                    {!isEmpty(presentationInfo?.header_data?.title?.icon) && (
                        <LockedHeaderIcon
                            cloudinaryId={presentationInfo?.header_data?.title?.icon}
                            height={MAGIC_ITEMS_LOCKED_HEADER_ICON_DIMENSIONS.HEIGHT}
                            width={MAGIC_ITEMS_LOCKED_HEADER_ICON_DIMENSIONS.WIDTH}
                        />
                    )}
                    <LockedTextContainer>
                        <LockedMessageText
                            variant="V2.body2.neutral.bold"
                            color={presentationInfo?.header_data?.title?.color as keyof IColors}
                        >
                            {replaceText(
                                presentationInfo?.header_data?.title?.text || "",
                                MAGIC_ITEMS_INLINE_ICON_KEY,
                                true,
                            )}
                        </LockedMessageText>
                        <Text
                            variant="V2.overline.neutral.bold"
                            color={presentationInfo?.header_data?.subtitle?.color as keyof IColors}
                        >
                            {replaceText(
                                presentationInfo?.header_data?.subtitle?.text || "",
                                MAGIC_ITEMS_INLINE_ICON_KEY,
                                true,
                            )}
                        </Text>
                    </LockedTextContainer>
                    {timerPillComponent}
                </LockedHeaderContainer>
            </MagicItemsHeaderContainer>
        );
    }

    // MAGIC ITEMS - UNLOCKED STATE
    if (isMagicItems && !isLocked) {
        const hasEOLockedMessage = !isEmpty(presentationInfo?.header_data?.title?.text);
        return (
            <MagicItemsHeaderContainer>
                {hasEOLockedMessage && (
                    <EOMessageWrapper>
                        {replaceText(
                            presentationInfo?.header_data?.title?.text || "",
                            MAGIC_ITEMS_INLINE_ICON_KEY,
                            true,
                        )}
                    </EOMessageWrapper>
                )}
                <MagicItemsHeaderWrapper hasTopPadding={!hasEOLockedMessage}>
                    <MagicItemsTitle>
                        <MagicItemsText variant="V2.overline.neutral.bold_spaced">
                            {replaceText(title || SURPRISE_DEALS_TITLE, MAGIC_ITEMS_INLINE_ICON_KEY, true)}
                        </MagicItemsText>
                    </MagicItemsTitle>
                    {timerPillComponent}
                </MagicItemsHeaderWrapper>
            </MagicItemsHeaderContainer>
        );
    }

    // NORMAL CROSS-SELL HEADER
    return (
        <HeaderContainer ref={containerRef}>
            {canPlayLottie ? (
                <HeaderLottieContainer>
                    <LottieAnimation
                        {...lottieProps}
                        fileName={getCloudinaryUrl(presentationInfo?.header_data?.lottie) || ""}
                        visible={true}
                        autoplay={playLottie}
                        playIfVisible={false}
                        playLottie={playLottie}
                        preserveAspect="xMinYMid slice"
                    />
                </HeaderLottieContainer>
            ) : (
                <TitleRowWrapper>
                    <TitleWrapper>
                        {!isEmpty(presentationInfo?.header_data?.title?.icon) && (
                            <Image
                                cloudinaryId={presentationInfo?.header_data?.title?.icon}
                                height={CROSS_SELL_HEADER_ICON_DIMENSIONS.HEIGHT}
                                width={CROSS_SELL_HEADER_ICON_DIMENSIONS.WIDTH}
                            />
                        )}
                        {replaceText(presentationInfo?.header_data?.title?.text || "", "", true)}
                    </TitleWrapper>
                </TitleRowWrapper>
            )}

            <HeaderText
                color="text_High_Emphasis"
                variant="V2.body2.neutral.medium"
                shouldAnimateChars={showCharacterAnimation}
            >
                {playLottie
                    ? // This is done to animate the characters one by one when we show lottie
                      text.split("").map((char, index) => (
                          <span key={index} className="char" style={{ "--char-index": index } as React.CSSProperties}>
                              {char === " " ? "\u00A0" : char}
                          </span>
                      ))
                    : text}
            </HeaderText>
        </HeaderContainer>
    );
};
